// @erp/module-cms/schemas — THE LOCALE CONTRACT (decided by the owner 2026-09-14,
// decisions.md "The locale contract"). Browser-safe: zod + Intl only, no env — the admin
// forms, the gateway and the site's mirror all parse the SAME rule, so what an editor is
// told is a valid locale is exactly what the server stores and the site serves.
//
// A locale identifier is a BCP 47 (RFC 5646) tag in CANONICAL form:
//   • `Intl.getCanonicalLocales(tag)` is the grammar — it throws on a malformed tag
//     ('x', 'en_US', '', whitespace) and returns the canonical case (language lowercase,
//     Script Titlecase, REGION upper): 'HI' → 'hi', 'pa-guru' → 'pa-Guru', 'en-in' → 'en-IN';
//   • the LANGUAGE subtag must be a REGISTERED ISO 639 code — `Intl.DisplayNames` (the
//     ICU registry workerd, V8 and every browser carry) must know a name for it that is not
//     the code itself ('hi' → "Hindi"; 'xq' → "xq", 'english' → "english" → rejected), and
//     the four special codes with no linguistic content (und / mul / zxx / mis) are refused
//     by name — ICU calls 'und' "root", which is a name but not a language;
//   • at most LOCALE_MAX characters, trimmed first.
// Stored values are the canonical tag ('en', 'hi'; 'pa-Guru' only when two variants of a
// language must coexist). Every locale that enters the system — a row's locale, a ?locale
// filter, the enabled-locales setting, the install's DEFAULT_LOCALE var — passes through
// `Locale` below.
import { z } from "zod";

/** The longest tag accepted (RFC 5646 tags are unbounded in theory; 35 covers every registered language-script-region-variant form). */
export const LOCALE_MAX = 35;

/** ISO 639 special codes that name NO language — refused even though ICU has a label for them. */
const NON_LANGUAGES = new Set(["und", "mul", "zxx", "mis"]);

/** The language subtag of a canonical tag — everything before the first '-'. */
export const localeLanguage = (tag: string): string => tag.split("-", 1)[0] ?? tag;

/**
 * The canonical form of a locale tag, or null when it is malformed or its language subtag
 * is not a registered ISO 639 code. The ONE rule; `Locale` below is its Zod face.
 */
export const canonicalLocale = (raw: string): string | null => {
  let tag: string;
  try {
    tag = Intl.getCanonicalLocales(raw)[0] ?? "";
  } catch {
    return null;
  }
  if (!tag) return null;
  const language = localeLanguage(tag);
  if (NON_LANGUAGES.has(language)) return null;
  // `fallback: "none"` answers undefined for a code the registry does not know; an older ICU
  // that ignores the option echoes the code itself — both read as "not registered".
  let name: string | undefined;
  try {
    name = new Intl.DisplayNames(["en"], { type: "language", fallback: "none" }).of(language);
  } catch {
    return null;
  }
  if (!name || name === language) return null;
  return tag;
};

/**
 * A locale's NATIVE name — the language named in itself ('hi' → "हिन्दी", 'en' → "English",
 * 'pa-Guru' → "ਪੰਜਾਬੀ (ਗੁਰਮੁਖੀ)"), computed on read from ICU and never stored. The tag itself
 * when ICU has no label (a canonical tag always has one for its language; the fallback is
 * for a stored value that predates this contract).
 */
export const localeNativeName = (tag: string): string => {
  try {
    return new Intl.DisplayNames([tag], { type: "language" }).of(tag) ?? tag;
  } catch {
    return tag;
  }
};

/**
 * The Locale schema: trim → bound → canonicalise → refine. Output is the CANONICAL tag, so
 * a caller that parses 'HI' stores and compares 'hi'. A malformed or unregistered tag is a
 * Zod issue (a 422 at the router, an inline field error in a form).
 */
export const Locale = z
  .string()
  .trim()
  .min(2)
  .max(LOCALE_MAX)
  .transform((raw, ctx) => {
    const tag = canonicalLocale(raw);
    if (tag === null) {
      ctx.addIssue({ code: "custom", message: `"${raw}" is not a BCP 47 locale tag with a registered language` });
      return z.NEVER;
    }
    return tag;
  });
export type Locale = z.infer<typeof Locale>;

// ── The enabled-locale LIST (item 3) — a cms_setting row, key "locales" ──────────────────

/**
 * A list of enabled locales — canonical, unique (after canonicalisation: 'HI' and 'hi' are
 * one entry, refused as a duplicate), non-empty. Whether it contains the install's
 * DEFAULT_LOCALE is the SERVICE's check (the schema knows no env).
 */
export const LocaleList = z
  .array(Locale)
  .min(1, "at least one locale must stay enabled")
  .max(64)
  .refine((xs) => new Set(xs).size === xs.length, { message: "locales must be unique" });
export type LocaleList = z.infer<typeof LocaleList>;

/** The cms_setting key the enabled-locale list lives under (a JSON array of canonical tags; seeded by db-cms 0020). */
export const LOCALES_SETTING_KEY = "locales";

/**
 * The stored form ↔ the parsed list. The settings store speaks TEXT scalars end to end
 * (schemas/settings.ts), so the value is a JSON array string; these two are the ONE reader
 * and the ONE writer of that string. `parseLocalesSetting` is lenient about the stored
 * SHAPE (a hand-edited row must never 500 a read — it yields what parses, and the read
 * rule re-adds the default) but strict about each tag: a malformed entry is dropped.
 */
export const parseLocalesSetting = (value: string | undefined): string[] => {
  if (!value) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const x of raw) {
    const tag = typeof x === "string" ? canonicalLocale(x.trim()) : null;
    if (tag && !out.includes(tag)) out.push(tag);
  }
  return out;
};

export const serializeLocalesSetting = (locales: readonly string[]): string => JSON.stringify(locales);

/**
 * The settings-store validator for the `locales` key: the JSON string a PATCH sends, parsed
 * through LocaleList and RE-SERIALISED canonical — so `'["HI","hi"]'` is a validation error
 * (a duplicate once canonical), `'["HI"]'` is stored exactly as `["hi"]`, and `'[]'` is
 * refused. A string in, a string out: the settings wire stays `Record<key, string>`.
 */
export const LocalesSettingValue = z
  .string()
  .max(4000)
  .transform((value, ctx) => {
    let raw: unknown;
    try {
      raw = JSON.parse(value);
    } catch {
      ctx.addIssue({ code: "custom", message: "locales must be a JSON array of locale tags" });
      return z.NEVER;
    }
    const parsed = LocaleList.safeParse(raw);
    if (!parsed.success) {
      ctx.addIssue({ code: "custom", message: parsed.error.issues[0]?.message ?? "locales must be a JSON array of locale tags" });
      return z.NEVER;
    }
    return serializeLocalesSetting(parsed.data);
  });

/** The per-table row counts behind a CMS_LOCALE_IN_USE refusal — live AND trashed rows, the seven per-locale tables (publicationTitles since 0022). */
export const LocaleUsage = z.object({
  announcements: z.number().int().nonnegative(),
  tenders: z.number().int().nonnegative(),
  events: z.number().int().nonnegative(),
  publications: z.number().int().nonnegative(),
  publicationTitles: z.number().int().nonnegative(),
  gallery: z.number().int().nonnegative(),
  categories: z.number().int().nonnegative(),
});
export type LocaleUsage = z.infer<typeof LocaleUsage>;
export const LOCALE_USAGE_TABLES = ["announcements", "tenders", "events", "publications", "publicationTitles", "gallery", "categories"] as const;

/** Total rows across the seven counts — 0 means the locale may be removed. */
export const localeUsageTotal = (u: LocaleUsage): number => LOCALE_USAGE_TABLES.reduce((n, t) => n + u[t], 0);

// ── GET /settings/public — the locale block the site reads (item 5) ─────────────────────

/**
 * Beside the allowlisted string keys, the public settings read carries the install's
 * default, the enabled list (always containing the default — the read rule) and each
 * enabled locale's NATIVE name (localeNativeName, computed on read, never stored). The site
 * mirrors this object with its own Zod (ERP-PUBLIC-WEBSITE §2.3) and fails its build on a
 * mismatch; changes here are ADDITIVE within a major version.
 */
export const CmsPublicLocales = z.object({
  defaultLocale: z.string(),
  locales: z.array(z.string()).min(1),
  localeNames: z.record(z.string(), z.string()),
});
export type CmsPublicLocales = z.infer<typeof CmsPublicLocales>;

/** The locale block for an enabled list — names computed per tag, in list order. */
export const publicLocalesOf = (defaultLocale: string, enabled: readonly string[]): CmsPublicLocales => ({
  defaultLocale,
  locales: [...enabled],
  localeNames: Object.fromEntries(enabled.map((tag) => [tag, localeNativeName(tag)])),
});
